<template lang="pug">
.banner-block-editor
  .editor-header
    h3 {{ isCreating ? 'New Banner' : 'Banner' }}

  .editor-form
    el-form(:model="formData" label-position="top" :rules="rules" ref="formRef")
      el-form-item(label="Full Width" prop="setFullWidth")
        el-checkbox(v-model="formData.setFullWidth") Display banner in full width

      .banner-items-section
        h4 Banners
        .banner-items-list(v-if="formData.items.length > 0")
          draggable.banner-items-draggable(
            v-model="formData.items"
            handle=".drag-handle"
            ghost-class="ghost-banner-item"
            item-key="id"
            @start="dragStart"
            @end="dragEnd"
          )
            template(#item="{ element: item, index }")
              .banner-item(:key="item.id")
                .banner-item-header
                  .drag-handle.cursor-move
                    fai(type="fas" icon="grip-vertical").size-14
                  h5 Item {{ index + 1 }}
                  .banner-item-actions
                  el-button(
                    :type="item.isEnabled ? 'primary' : 'info'"
                    size="small"
                    text
                    @click="toggleItemVisibility(index)"
                    title="Toggle visibility"
                  ).has-fai
                    fai(:type="item.isEnabled ? 'fal' : 'fas'" icon="eye").size-14
                  el-button(
                    type="danger"
                    size="small"
                    text
                    @click="removeItem(index)"
                  ).has-fai
                    fai(type="fal" icon="trash").size-14
                el-form-item(
                  label="Image"
                  :prop="`items.${index}.imageUrl`"
                  :rules="{ required: true, message: 'Image is required', trigger: 'change' }"
                )
                  template(#label)
                    span Image
                    .helper-text &nbsp; Recommended: 1200x600px (2:1 ratio) for best quality
                  .image-upload-container
                    .image-preview(v-if="item.imageUrl")
                      img(:src="item.imageUrl" alt="Banner image preview")
                      .image-actions
                        el-button(type="primary" size="small" @click="triggerFileInput(index)") Change
                    el-upload(
                      :ref="(el) => setUploadRef(el, index)"
                      drag
                      :show-file-list="false"
                      :before-upload="beforeUpload"
                      :http-request="(payload) => handleImageUpload(payload, index)"
                      v-loading="uploading.image && formData.currentImageIndex === index"
                      action=""
                      accept="image/*"
                      :class="{'img-uploader': true, 'img-uploader--hidden': item.imageUrl, 'pos-r': true, 'mb-0': true}"
                    )
                      template(v-if="!item.imageUrl")
                        img(src="@/assets/icons/file-image-outline.svg").img-uploader-icon
                        p.upload-picture-text Drop an image file here or
                        div.fake-upload-button Browse For File
                el-form-item.mt-0(
                  label="Deep Link"
                  :prop="`items.${index}.deepLink`"
                )
                  template(#label)
                    span Deep Link
                    .helper-text &nbsp; You can get links to stickers, users, or organizations from their details in the CMS.
                  el-input(
                    v-model="item.deepLink"
                    placeholder="Enter deep link URL (optional)"
                    size="default"
                  )
                .item-divider(v-if="index < formData.items.length - 1")
        .no-items(v-else)
          p No banner items added yet. Add an item below.
        .add-item-button
          el-button(
            type="primary"
            icon="el-icon-plus"
            @click="addItem"
            size="default"
          ) Add Banner Item

      .editor-actions
        el-button(
          @click="handleCancel"
          size="default"
        ) Cancel
        el-button(
          type="primary"
          @click="handleSave"
          size="default"
          :loading="saving"
        ) Save
</template>

<script>
import {
  defineComponent, ref, reactive,
} from 'vue';
import draggable from 'vuedraggable';
import { ElMessage } from 'element-plus';
import useStaticPageUtils from '../shared/index.ts';
import fileUpload from '@/shared/upload';
import { resizeAndConvertToPNG } from '@/shared/utility-functions';
import fai from '@/plugins/font-awesome';

export default defineComponent({
  name: 'BannerBlock',
  components: { fai, draggable },
  props: {
    block: {
      type: Object,
      default: null,
    },
    isCreating: {
      type: Boolean,
      default: false,
    },
    pageId: {
      type: String,
      required: true,
    },
    staticPageData: {
      type: Object,
      default: null,
    },
  },
  emits: ['save', 'cancel'],
  setup(props, { emit }) {
    const { createOrUpdateBannerBlock } = useStaticPageUtils();
    const formRef = ref(null);
    const uploadRefs = ref({});
    const saving = ref(false);
    const { uploadStatus, beforeUpload, handleUploadRequest } = fileUpload('stickers', 'Banner');
    const isUploading = reactive({
      image: false,
    });

    // Initialize form data with existing values or defaults
    const formData = reactive({
      setFullWidth: props.block?.details?.setFullWidth || false,
      items: props.block?.details?.items?.map((item, index) => ({
        id: item.id || `item-${Date.now()}-${index}`,
        imageUrl: item.imageUrl || '',
        deepLink: item.deepLink || '',
        isEnabled: item.isEnabled !== undefined ? item.isEnabled : true,
      })) || [],
      currentImageIndex: -1,
    });

    // Form validation rules
    const rules = {
      items: {
        type: 'array',
        required: true,
        message: 'Please add at least one banner item',
        trigger: 'change',
        validator: (rule, value) => value.length > 0,
      },
    };

    // Add a new empty item
    function addItem() {
      formData.items.push({
        id: `item-${Date.now()}-${formData.items.length}`,
        imageUrl: '',
        deepLink: '',
        isEnabled: true,
      });
      // Set the current index to the newly added item
      formData.currentImageIndex = formData.items.length - 1;
    }

    // Handle image upload
    async function handleImageUpload(payload, index) {
      try {
        isUploading.image = true;
        formData.currentImageIndex = index;

        // Process image before upload
        if (payload.file && payload.file.type.startsWith('image/')) {
          try {
            const processedImage = await resizeAndConvertToPNG(payload.file, 1200, 600, 0.9);
            const newPayload = { ...payload };
            newPayload.file = processedImage;
            payload = newPayload;
          } catch (processingError) {
            console.error('Error processing image:', processingError);
            // Continue with original file if processing fails
          }
        }

        // Upload the image
        await handleUploadRequest(payload);

        if (uploadStatus.status === 'ok') {
          // Use the resourceUrl from the server instead of blob URL
          formData.items[index].imageUrl = uploadStatus.resourceUrl;
          ElMessage.success('Image uploaded successfully');
        } else {
          ElMessage.error(uploadStatus.message || 'Failed to upload image');
        }
      } catch (error) {
        console.error('Error uploading banner image:', error);
        ElMessage.error('Failed to upload image');
      } finally {
        isUploading.image = false;
      }
    }

    // Before upload validation is handled by the fileUpload utility

    // Remove an item by index
    function removeItem(index) {
      formData.items.splice(index, 1);
    }

    // Toggle item visibility
    function toggleItemVisibility(index) {
      formData.items[index].isEnabled = !formData.items[index].isEnabled;
    }

    // Handle save
    async function handleSave() {
      if (!formRef.value) return;

      try {
        await formRef.value.validate();
        saving.value = true;

        const input = {
          setFullWidth: formData.setFullWidth,
          items: formData.items.map((item) => ({
            imageUrl: item.imageUrl,
            deepLink: item.deepLink,
            isEnabled: item.isEnabled,
          })),
          sortOrder: props.isCreating ? null : (props.block?.sortOrder || 0),
          parentId: props.pageId,
          id: props.isCreating ? null : props.block?.id,
        };

        const result = await createOrUpdateBannerBlock(input, props.staticPageData);

        ElMessage.success(props.isCreating ? 'Banner block created successfully' : 'Banner block updated successfully');
        emit('save', result);
      } catch (error) {
        console.error('Error saving banner block:', error);
        ElMessage.error('Failed to save banner block');
      } finally {
        saving.value = false;
      }
    }

    // Drag event handlers
    function dragStart() {
      // Optional: Add any logic needed when dragging starts
    }

    function dragEnd() {
      // Optional: Add any logic needed when dragging ends
    }

    // Handle cancel
    function handleCancel() {
      emit('cancel');
    }

    // Set upload ref for each upload component
    function setUploadRef(el, index) {
      if (el) {
        uploadRefs.value[index] = el;
      }
    }

    // Trigger file input click when Change button is clicked
    function triggerFileInput(index) {
      // Set current index for upload
      formData.currentImageIndex = index;

      // Find the upload input in the current item and trigger click
      if (uploadRefs.value[index]) {
        const uploadInput = uploadRefs.value[index].$el.querySelector('input[type="file"]');
        if (uploadInput) {
          uploadInput.click();
        }
      }
    }

    return {
      formRef,
      uploadRefs,
      formData,
      rules,
      saving,
      uploading: isUploading,
      addItem,
      removeItem,
      toggleItemVisibility,
      handleSave,
      handleCancel,
      handleImageUpload,
      beforeUpload,
      setUploadRef,
      triggerFileInput,
      dragStart,
      dragEnd,
    };
  },
});
</script>

<style scoped>
.banner-block-editor {
  width: 100%;
}

.editor-form {
  max-width: 700px;
  margin-top: 20px;
}

.banner-items-section {
  margin-top: 24px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 16px;
  background-color: #f9f9f9;
}

.banner-items-list {
  margin-top: 16px;
}

.banner-item {
  background-color: #fff;
  border-radius: 4px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.banner-item-header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
}

.banner-item-header h5 {
  flex: 1;
}

.drag-handle {
  cursor: move; /* fallback */
  cursor: grab;
  color: #909399;
  margin-right: 4px;
  display: flex;
  align-items: center;
  padding: 8px;
  background-color: #f5f7fa;
  border-radius: 4px;
  transition: all 0.3s;
}

.drag-handle:hover {
  color: #1EBEAF;
  background-color: #e6f7f6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.drag-handle:active {
  cursor: grabbing;
}

.ghost-banner-item {
  opacity: 0.5;
  background: #e6f7f6;
  border: 1px dashed #1EBEAF;
}

.reorder-hint {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #909399;
  margin-bottom: 12px;
}

.banner-items-draggable {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.banner-item-header h5 {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
}

.item-divider {
  height: 1px;
  background-color: #ebeef5;
  margin: 24px 0;
}

.image-upload-container {
  width: 100%;
}

.image-preview {
  position: relative;
  margin-bottom: 0;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;
  max-width: 300px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.image-preview img {
  width: 100%;
  height: auto;
  display: block;
}

.image-preview .image-actions {
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 8px;
  background-color: rgba(0, 0, 0, 0.5);
  border-top-left-radius: 4px;
}

.img-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
  transition: border-color 0.3s;
}

.img-uploader:hover {
  border-color: #1EBEAF;
}

.img-uploader--hidden {
  height: 0;
  padding: 0;
  margin: 0;
  border: none;
  overflow: hidden;
  opacity: 0;
}

.img-uploader-icon {
  width: 40px;
  height: 40px;
  margin-bottom: 8px;
}

.upload-picture-text {
  color: #606266;
  font-size: 14px;
  margin: 8px 0;
}

.fake-upload-button {
  color: #1EBEAF;
  font-size: 14px;
  font-weight: 500;
  margin-top: 8px;
  cursor: pointer;
}

.no-items {
  text-align: center;
  padding: 24px;
  color: #909399;
}

.add-item-button {
  margin-top: 16px;
  text-align: center;
}

.editor-actions {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.mr-2 {
  margin-right: 8px;
}

.helper-text {
  font-size: 12px;
  color: #909399;
  font-weight: normal;
  margin-top: 4px;
  line-height: 1.4;
}
</style>
